/**
 * Reads the models an account can use out of Suno's billing/info response.
 *
 * suno.com's own code shows a model entry as an object with `external_key`, a `name`, a `major_version`,
 * `badges` (a custom model has "custom"), `can_use`, and for custom models `custom_model_base_model` and
 * `custom_model_ownership`; the entry's `id` is the custom_model_id. Where those entries sit inside the
 * response was NOT visible in the static code, so this walks the whole JSON and picks up anything shaped
 * like one, instead of assuming a path. Only the fields below are kept; nothing else from the response
 * (plan, credits, account details) is read out.
 */

export interface SunoModelEntry {
  /** The model's id. For a custom model this is its custom_model_id. */
  id: string | null;
  name: string;
  /** What Suno's client calls the model's key (the generate request's `mv` for built-in models). */
  externalKey: string;
  majorVersion: string | null;
  baseModel: string | null;
  /** "owned" / "shared" style value for custom models, null for built-in ones. */
  ownership: string | null;
  badges: string[];
  canUse: boolean | null;
  isDefault: boolean;
  description: string;
  isCustom: boolean;
}

const MAX_DEPTH = 10;

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

function toEntry(o: Record<string, unknown>): SunoModelEntry | null {
  const externalKey = str(o.external_key);
  if (!externalKey) return null;
  const badges = Array.isArray(o.badges) ? o.badges.filter((b): b is string => typeof b === 'string') : [];
  const baseModel = str(o.custom_model_base_model);
  const ownership = str(o.custom_model_ownership);
  return {
    id: str(o.id) ?? str(o.custom_model_id),
    name: str(o.name) ?? str(o.display_name) ?? externalKey,
    externalKey,
    majorVersion: str(o.major_version),
    baseModel,
    ownership,
    badges,
    canUse: typeof o.can_use === 'boolean' ? o.can_use : null,
    isDefault: o.is_default_model === true || o.is_default_free_model === true,
    description: str(o.description) ?? '',
    isCustom: badges.includes('custom') || !!baseModel || !!ownership,
  };
}

/** Every model entry found anywhere in `json`, de-duplicated, custom models first. */
export function extractSunoModels(json: unknown): SunoModelEntry[] {
  const found = new Map<string, SunoModelEntry>();

  const walk = (node: unknown, depth: number) => {
    if (depth > MAX_DEPTH || node === null || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const item of node) walk(item, depth + 1);
      return;
    }
    const obj = node as Record<string, unknown>;
    const entry = toEntry(obj);
    if (entry) {
      const key = `${entry.id ?? ''}|${entry.externalKey}`;
      if (!found.has(key)) found.set(key, entry);
    }
    for (const value of Object.values(obj)) walk(value, depth + 1);
  };
  walk(json, 0);

  return [...found.values()].sort((a, b) => Number(b.isCustom) - Number(a.isCustom) || a.name.localeCompare(b.name));
}

/**
 * The top-level key names of a response (and how many items any array holds), with NO values. Shown when
 * no model entries are found, so the real shape can be learned without exposing account details.
 */
export function describeResponseShape(json: unknown): string[] {
  if (json === null || typeof json !== 'object') return [typeof json];
  if (Array.isArray(json)) return [`(array of ${json.length})`];
  return Object.entries(json as Record<string, unknown>).map(([k, v]) =>
    Array.isArray(v) ? `${k} [${v.length}]` : v !== null && typeof v === 'object' ? `${k} {…}` : k,
  );
}
